import { useEffect, useState, type ReactElement } from 'react';
import { Alert, AlertTitle } from '@mui/material';

/**
 * Maintenance banner for the 2026-05-08 → 2026-05-09 cutover window.
 * Visible at the top of the routed content area until either:
 *   1. The user dismisses it (per-window localStorage flag), OR
 *   2. The window's end time passes (auto-hide via 60s tick).
 *
 * Hardcoded against this specific cutover window — intentionally a
 * one-shot component, not a generic CMS-driven banner. When the next
 * maintenance window comes, bump WINDOW_KEY (so previously-dismissed
 * users see the new notice) and update WINDOW_END_UTC.
 *
 * Window: Friday 2026-05-08 5:00 PM EST → Saturday 2026-05-09 10:00 AM EST
 *   * Friday 5:00 PM – ~9:00 PM: prep / baseline (no outage)
 *   * Friday ~9:00 PM onwards: extended cutover work; user-visible
 *     outages of varying length expected through Saturday 10:00 AM.
 *
 * Bumped WINDOW_KEY to v2 so users who dismissed the original
 * "10:30 PM" banner see the new extended-window notice.
 */
const WINDOW_KEY = 'qfly:maintenance-banner-dismissed:2026-05-08-v2';
// 10:00 AM EST Sat May 9, 2026 == 14:00 UTC May 9, 2026
const WINDOW_END_UTC = '2026-05-09T14:00:00Z';

export const MaintenanceBanner = (): ReactElement | null => {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(WINDOW_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [now, setNow] = useState<number>(() => Date.now());

  // Re-check expiry every minute. 60s is enough resolution for a
  // multi-hour maintenance window — no need to tick faster.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const expiresAt = new Date(WINDOW_END_UTC).getTime();
  if (now >= expiresAt) return null;
  if (dismissed) return null;

  const handleDismiss = (): void => {
    try {
      window.localStorage.setItem(WINDOW_KEY, 'true');
    } catch {
      // localStorage unavailable / quota exceeded — banner just won't
      // remember the dismissal across reloads. Still hides for this session.
    }
    setDismissed(true);
  };

  return (
    <Alert
      severity="warning"
      variant="filled"
      onClose={handleDismiss}
      sx={{
        borderRadius: 0,
        mb: 2,
        '& .MuiAlert-message': { width: '100%' },
      }}
      role="status"
      aria-live="polite"
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        Scheduled maintenance — Friday, May 8, 5:00 PM EST → Saturday, May 9, 10:00 AM EST
      </AlertTitle>
      We are migrating Qfly to a new infrastructure stack tonight. Service
      may be intermittent throughout this window, with periods of
      unavailability while we cut over. Please save your work before
      starting and avoid creating new opportunities or stage changes
      after 9:00 PM EST tonight. Apologies for the inconvenience —
      questions to <strong>gerry.singh@cloudeq.com</strong>.
    </Alert>
  );
};
